import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UndoIcon from "@mui/icons-material/Undo";
import {
	Box,
	Button,
	IconButton,
	InputAdornment,
	Snackbar,
	TextField,
	Tooltip
} from "@mui/material";
import { DateTime } from "luxon";
import { Moment } from "moment";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { WebStoreContext } from "../../../contexts/WebStoreProvider";

interface ServerTimeControlProps {
	disabled?: boolean;
	onReset: () => Promise<void>;
	onSet: (now: string) => Promise<void>;
}

const getPacificTime = (time: Moment | null) =>
	time
		? DateTime.fromISO(time.toISOString())
				.setZone("America/Los_Angeles")
				.toISO() ?? ""
		: "";

const ServerTimeControl: React.FC<ServerTimeControlProps> = ({
	disabled = false,
	onReset,
	onSet
}) => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const { nowTime } = useContext(WebStoreContext);
	const [fieldValue, setFieldValue] = useState(() => getPacificTime(nowTime));
	const [isEditing, setIsEditing] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const [serverTimeCopied, setServerTimeCopied] = useState(false);
	const isBusy = disabled;
	const appliedTime = getPacificTime(nowTime);
	const hasChanges = isDirty && fieldValue !== appliedTime;

	useEffect(() => {
		if (!isEditing && !isDirty) {
			setFieldValue(getPacificTime(nowTime));
		}
	}, [isDirty, isEditing, nowTime]);

	useEffect(() => {
		const timeJump = new URLSearchParams(search).get("timeJump");

		if (!timeJump) {
			return;
		}

		navigate(window.location.pathname, { replace: true });
		void onSet(timeJump);
	}, []);

	const handleSetServerTime = async () => {
		if (!hasChanges || isBusy) {
			return;
		}

		await onSet(fieldValue);
	};

	const handleResetServerTime = async () => {
		if (isBusy) {
			return;
		}

		await onReset();
	};

	const handleDiscardChanges = () => {
		setFieldValue(appliedTime);
		setIsDirty(false);
	};

	const handleCopyServerTime = async () => {
		if (!fieldValue) {
			return;
		}

		try {
			const timeJumpUrl = new URL(window.location.href);
			timeJumpUrl.searchParams.set("timeJump", fieldValue);
			await navigator.clipboard.writeText(timeJumpUrl.toString());
			setServerTimeCopied(true);
		} catch (error) {
			console.error("Failed to copy Server Now:", error);
		}
	};

	return (
		<Box sx={{ display: "grid", width: "100%" }}>
			<TextField
				color={hasChanges ? "warning" : "primary"}
				disabled={isBusy}
				fullWidth
				helperText={hasChanges ? "Changes not applied" : " "}
				label="Server Now"
				onChange={(event) => {
					setFieldValue(event.target.value);
					setIsDirty(true);
				}}
				onBlur={() => setIsEditing(false)}
				onFocus={() => setIsEditing(true)}
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						event.preventDefault();
						handleSetServerTime();
					}
				}}
				value={fieldValue}
				FormHelperTextProps={{
					sx: {
						color: hasChanges ? "warning.main" : undefined,
						marginTop: 0
					}
				}}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							{hasChanges && (
								<Tooltip title="Discard changes">
									<IconButton
										aria-label="Discard Server Now changes"
										disabled={isBusy}
										onClick={handleDiscardChanges}
									>
										<UndoIcon />
									</IconButton>
								</Tooltip>
							)}
							<IconButton
								aria-label="Copy Server Now link"
								disabled={isBusy || !fieldValue}
								edge="end"
								onClick={handleCopyServerTime}
							>
								<ContentCopyIcon />
							</IconButton>
						</InputAdornment>
					)
				}}
				sx={
					hasChanges
						? {
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "warning.main"
								}
						  }
						: undefined
				}
			/>
			<Box
				sx={{
					display: "flex",
					gap: "0.5rem",
					width: "100%"
				}}
			>
				<Button
					color={hasChanges ? "warning" : "primary"}
					disabled={isBusy || !hasChanges}
					onClick={handleSetServerTime}
					sx={{ flex: 1 }}
					variant={hasChanges ? "contained" : "outlined"}
				>
					Set
				</Button>
				<Button
					disabled={isBusy}
					onClick={handleResetServerTime}
					sx={{ flex: 1 }}
					variant="outlined"
				>
					Reset
				</Button>
			</Box>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				autoHideDuration={2000}
				message="Server Now link copied to clipboard"
				onClose={() => setServerTimeCopied(false)}
				open={serverTimeCopied}
			/>
		</Box>
	);
};

export default ServerTimeControl;
